import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BookOpen, Copy, Check, ExternalLink } from "lucide-react";
import { trackEbookEvent } from "@/api/aimeansbusiness";

const PROMO_CODE = "BNPAIMB2026";
const EXTERNAL_URL = "https://www.barnesandnoble.com/w/the-artificial-intelligence-survival-guide-for-dr-synthia-speaks/1146009083?ean=2940186028857";
const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_ai-value-pricing/artifacts/fnse5idx_AI_Survival_Guide_For_Humans_Front_Cover_Small.jpg";

/**
 * EbookPromoModal - Reveals promo code and redirects to Barnes & Noble
 * 
 * This modal is shown only to qualified users (completed evaluation + onboarding).
 * It reveals the promo code and handles the redirect on confirmation.
 */
const EbookPromoModal = ({ open, onOpenChange, serId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(PROMO_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleConfirm = async () => {
    // Track outbound click before redirect
    try {
      await trackEbookEvent("ebook_outbound_click", serId);
    } catch (err) {
      console.error("Failed to track ebook outbound click:", err);
    }
    
    // Close modal
    onOpenChange(false);
    
    // Open external link in new tab
    window.open(EXTERNAL_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-navy-900 border-slate-700">
        <DialogHeader>
          <div className="flex flex-col items-center mb-4">
            {/* Book Cover Image */}
            <img 
              src={BOOK_COVER_URL} 
              alt="The A.I. Survival Guide For Humans" 
              className="w-32 h-auto rounded-lg shadow-lg mb-4"
            />
            <DialogTitle className="font-heading text-xl text-white text-center">
              Your Free Ebook
            </DialogTitle>
          </div>
          <DialogDescription className="text-slate-300 text-base leading-relaxed text-center">
            Use the promo code below at checkout to receive your free ebook copy.
            Please save this code— it is required to complete your order.
          </DialogDescription>
        </DialogHeader>

        {/* Promo Code Display */}
        <div className="my-6">
          <p className="text-slate-400 text-sm mb-2 text-center">Your Promo Code:</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-navy-950 border border-primary/30 rounded-lg p-4 text-center">
              <span className="font-mono text-2xl font-bold text-primary tracking-wider" data-testid="promo-code">
                {PROMO_CODE}
              </span>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyCode}
              className="h-14 w-14 border-slate-700 hover:bg-slate-800"
              data-testid="copy-code-btn"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-slate-400" />
              )}
            </Button>
          </div>
        </div>

        {/* Note */}
        <div className="bg-slate-800/50 rounded-lg p-3 mb-6">
          <p className="text-slate-400 text-sm leading-relaxed">
            This code is valid only when entered at checkout on Barnes & Noble.
          </p>
        </div>

        {/* Confirm Button */}
        <Button
          onClick={handleConfirm}
          className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-5"
          data-testid="ebook-confirm-btn"
        >
          Okay
          <ExternalLink className="ml-2 w-4 h-4" />
        </Button>
        
        <p className="text-slate-500 text-xs text-center mt-2">
          Clicking "Okay" will open Barnes & Noble in a new tab.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default EbookPromoModal;
